import Summary from "@/components/bootcamp/Summary";
import SelectCoupon from "@/components/coupons/SelectCoupon";
import GoldCard from "@/components/gold/Goldcard";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UseGetAmountReturn } from "@/hooks/getAmount";
import { UserLocalData } from "@/types/bootcamp-slug";
import {
  PAYMENT_METHOD_TYPES,
  PaymentTypeV2,
  purchaseBootcamp,
} from "@/utils/bootcamp";
import mixpanel from "@/utils/mixpanel";
import StateFinder from "@/utils/stateFinder";
import webengage from "@/webengage";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AddressSelector } from "../userAdresses/SelectAddress";

export const BootcampSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  phone: z.string(),
  address: z
    .object({
      country: z.string().optional(),
      state: z.string().optional(),
      pincode: z.string(),
      addressLine1: z.string(),
      addressLine2: z.string().optional(),
      landMark: z.string().optional(),
      city: z.string(),
    })
    .optional(),
});

export default function BootcampForm({
  bootcamp,
  coupons,
  amount,
}: {
  bootcamp: any;
  amount: UseGetAmountReturn;
  coupons?: any[] | undefined;
}) {
  const {
    mainButtonAmount,
    BaseAmount,
    isToken,
    isDiscounted,
    discountedToken,
  } = amount;
  const [addPlan, setAddPlan] = useState<boolean>(
    bootcamp?.metaData?.goldcard === "true" ? true : false
  );
  const [price, setPrice] = useState(BaseAmount + (addPlan ? 499 : 0));
  const [isLoading, setIsLoading] = useState(false);
  const [userIp, setUserIp] = useState("");
  const router = useRouter();
  const [errorMessageSelectCoupons, setErrorMessageSelectCoupons] =
    useState("");

  const [isBunleBxSelected, setIsBundleBxSelected] = useState<boolean>(false);
  const [phoneValidationError, setPhoneValidationError] = useState<string>("");
  const [couponDiscount, setCouponDiscount] = useState<number>(0);
  const [finalCouponSelected, setFinalCouponSelected] =
    useState<any>(undefined);
  const [country, setCountry] = useState<string>("India");
  const [selectedState, setSelectedState] = useState<string>("Punjab");
  const stateFinder = useMemo(() => new StateFinder(), []);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null
  );

  const userDetails: UserLocalData | null =
    typeof localStorage !== "undefined"
      ? JSON.parse(String(localStorage.getItem("tfu-user-auth")))
      : null;

  const [phoneNumber, setPhoneNumber] = useState(userDetails?.phone || "");
  const [isSelectAddressEnabled, setIsSelectAddressEnabled] = useState<boolean>(
    userDetails?.phone?.length === 10
  );
  const [isSelectCouponEnabled, setIsSelectCouponEnabled] = useState<boolean>(
    userDetails?.phone?.length === 10
  );

  const allCoupons = useMemo(() => {
    let allCoupons: any[] = [];
    if (coupons) {
      for (const coupon of coupons) {
        coupon.isBootcampCoupon = true;
        allCoupons.push(coupon);
      }
    }
    return allCoupons;
  }, [coupons]);

  const form = useForm<z.infer<typeof BootcampSchema>>({
    resolver: zodResolver(BootcampSchema),
    defaultValues: {
      name: userDetails?.name || "",
      email: userDetails?.email || "",
      phone: userDetails?.phone || "",
      address: {
        country: userDetails?.country || "India",
        state: userDetails?.state || "Punjab",
        addressLine1: "",
        city: "",
        pincode: "",
      },
    },
  });

  const address = form.watch("address");

  const paymentMethodType:
    | {
        newPaymentMethodType: PaymentTypeV2;
        oldPaymentMethodType: PAYMENT_METHOD_TYPES;
      }
    | undefined = useMemo(() => {
    if (isToken) {
      return {
        newPaymentMethodType: PaymentTypeV2.TOKEN,
        oldPaymentMethodType: PAYMENT_METHOD_TYPES.TOKEN,
      };
    } else if (isDiscounted && !isToken) {
      return {
        newPaymentMethodType: PaymentTypeV2.DISCOUNTED,
        oldPaymentMethodType: PAYMENT_METHOD_TYPES.DISCOUNTED,
      };
    } else {
      return undefined;
    }
  }, [isToken, addPlan, isDiscounted]);

  const onSubmit = async (values: z.infer<typeof BootcampSchema>) => {
    if (/^\d+$/.test(phoneNumber) === false) {
      setPhoneValidationError("Phone number should contain only numbers");
      return;
    }
    if (phoneNumber?.length !== 10) {
      setPhoneValidationError("Phone number should be of 10 digits");
      return;
    }
    values.phone = phoneNumber ? phoneNumber : values?.phone;
    localStorage.setItem("tfu-user-auth", JSON.stringify(values));

    setIsLoading(true);
    await purchaseBootcamp({
      amount,
      values,
      finalCouponSelected,
      bootcamp: isBunleBxSelected ? bootcamp?.bundleBootcamp : bootcamp,
      addPlan,
      router,
      paymentMethodType,
      userIp,
      selectedAddressId,
    });
    setIsLoading(false);
  };

  const fetchUserIp = async () => {
    const { data } = await axios.get("https://api64.ipify.org/");
    setUserIp(data);
  };

  useEffect(() => {
    const _webengage = webengage();
    _webengage?.track("Bootcamps viewed", {
      title: bootcamp?.title,
      industry: bootcamp?.teacher?.category?.name,
      fees: Number(mainButtonAmount),
      instructor: bootcamp?.teacher?.name,
      duration: `${bootcamp?.duration} ${bootcamp?.durationType}`,
      source: "Astrolearn",
      date: bootcamp?.startDateTime && new Date(bootcamp?.startDateTime),
    });
    fetchUserIp();
  }, [bootcamp]);

  const [mobileCoupons, setMobileCoupons] = useState<any>([]);
  useEffect(() => {
    setMobileCoupons(JSON.parse(localStorage.getItem("coupons") ?? "[]"));
  }, [coupons]);

  const handleApplyCouponClick = () => {
    if (phoneNumber?.length !== 10) {
      setErrorMessageSelectCoupons("Please enter a valid phone number first");
      setIsSelectCouponEnabled(false);
      setIsSelectAddressEnabled(false);
      return;
    }
    setIsSelectCouponEnabled(true);
    setIsSelectAddressEnabled(true);
  };
  const handlePhoneChange = (e: any) => {
    setPhoneNumber(e.target.value);
    if (e.target.value?.length === 10) {
      setErrorMessageSelectCoupons("");
      setIsSelectCouponEnabled(true);
      setIsSelectAddressEnabled(true);
    } else {
      setErrorMessageSelectCoupons("Enter a valid phone number first");
      setIsSelectCouponEnabled(false);
      setIsSelectAddressEnabled(false);
      setSelectedAddressId(null);
    }
  };

  let isBundle = false;
  if (bootcamp?.bundleBootcampId) {
    isBundle = true;
  }

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.addEventListener("click", (e) => {
        if (e.target instanceof HTMLInputElement) {
          const _webengage = webengage();
          if (_webengage) {
            _webengage.track("bootcamp form edited", {
              title: bootcamp?.title,
              industry: bootcamp?.teacher?.category?.name,
              fees: Number(mainButtonAmount),
              instructor: bootcamp?.teacher?.name,
              duration: `${bootcamp?.duration} ${bootcamp?.durationType}`,
              source: "Astrolearn",
              date:
                bootcamp?.startDateTime && new Date(bootcamp?.startDateTime),
              field: e.target.name,
            });
          }
        }
      });
    }
  }, [formRef]);

  useEffect(() => {
    if (!stateFinder) return;
    (async () => {
      const detectedCountry = await stateFinder.getPossibleCountry();
      if (detectedCountry) {
        setCountry(detectedCountry);
        form.setValue("address.country", detectedCountry);
      } else {
        form.setValue("address.country", "India");
      }
      const detectedState = await stateFinder.getPossibleState();
      if (detectedState) {
        setSelectedState(detectedState);
        form.setValue("address.state", detectedState);
      } else {
        form.setValue("address.state", "Punjab");
      }
    })();
  }, [stateFinder, form]);

  return (
    <Form {...form}>
      <form
        ref={formRef}
        id="form"
        className="w-full bg-white rounded-lg border-2 border-blue-950 sticky top-12"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <h2 className="text-lg py-2 bg-blue-950 text-white uppercase text-center">
          Registration Form
        </h2>
        <div className="my-4 px-3 space-y-4">
          <FormField
            name="name"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="normal-case">
                  Name <sup className="text-red-500">*</sup>{" "}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter your name"
                    type="text"
                    disabled={isLoading}
                    className="!rounded-lg"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="email"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="normal-case">
                  Email <sup className="text-red-500">*</sup>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter your email"
                    type="text"
                    disabled={isLoading}
                    className="!rounded-lg"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="phone"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="normal-case">
                  Mobile Number (Whatsapp) <sup className="text-red-500">*</sup>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter your whatsapp number"
                    type="text"
                    disabled={isLoading}
                    className="!rounded"
                    onChange={handlePhoneChange}
                    value={phoneNumber}
                  />
                </FormControl>
                {phoneValidationError ? (
                  <span className="text-sm font-medium text-red-500">
                    {phoneValidationError}
                  </span>
                ) : (
                  <FormMessage />
                )}
              </FormItem>
            )}
          />
          <div className="flex gap-4">
            <div className="flex-1">
              <FormField
                name="address.country"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="normal-case">Country</FormLabel>
                    <Select
                      disabled={isLoading}
                      onValueChange={(value) => {
                        field.onChange(value);
                        setCountry(value);
                        if (value !== "India") {
                          form.setValue("address.state", "placeholder-state");
                          setSelectedState("placeholder-state");
                        }
                      }}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="placeholder-country">
                          Select Country
                        </SelectItem>
                        {stateFinder
                          ?.listCountries()
                          ?.map((country: string) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {country === "India" && (
              <div className="flex-1">
                <FormField
                  name="address.state"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="normal-case">State</FormLabel>
                      <Select
                        disabled={isLoading}
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a state" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="placeholder-state">
                            Select State
                          </SelectItem>
                          {stateFinder?.listStates()?.map((state: string) => (
                            <SelectItem key={state} value={state}>
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
          {bootcamp?.metaData?.collectAddressOnLead === true && (
            <AddressSelector
              phoneNumber={phoneNumber}
              isSelectAddressEnabled={isSelectAddressEnabled}
              selectedId={selectedAddressId}
              setSelectedId={setSelectedAddressId}
              form={form}
              stateFinder={stateFinder}
              isLoading={isLoading}
              country={country}
              setCountry={setCountry}
              setSelectedState={setSelectedState}
              address={address}
            />
          )}
          {!isToken && !isDiscounted ? (
            <div className="flex flex-col gap-1">
              <div
                onClick={handleApplyCouponClick}
                className="w-full h-full flex p-0"
              >
                <SelectCoupon
                  phoneNumber={phoneNumber}
                  isSelectCouponEnabled={isSelectCouponEnabled}
                  setFinalCouponSelected={setFinalCouponSelected}
                  setCouponDiscount={setCouponDiscount}
                  originalOrderAmount={parseInt(BaseAmount)}
                  coupons={allCoupons}
                  bootcampTitle={bootcamp?.title}
                />
              </div>
              <span className="text-red-500 text-sm">
                {errorMessageSelectCoupons}
              </span>
            </div>
          ) : null}
          {bootcamp?.metaData?.goldcard === "true" && !isToken && (
            <GoldCard
              price={parseInt(mainButtonAmount)}
              addPlan={addPlan}
              setAddPlan={setAddPlan}
              setPrice={setPrice}
              bootcampTitle={bootcamp?.title}
            />
          )}

          <Summary
            isToken={isToken}
            isDiscounted={isDiscounted}
            tokenAmount={parseInt(mainButtonAmount)}
            price={parseInt(mainButtonAmount)}
            addPlan={addPlan}
            bootcamp={bootcamp}
            couponDiscount={couponDiscount}
          />
          {isToken && (
            <div className="text-foreground text-sm font-medium">
              This is a token payment. Please pay{" "}
              <span className="font-bold">₹{mainButtonAmount}</span> now and the
              remaining amount of{" "}
              <span className="font-bold">
                ₹{parseInt(BaseAmount) - parseInt(mainButtonAmount, 10)}
              </span>{" "}
              will be due later.
            </div>
          )}
          {isToken ? (
            <Button className="w-full text-lg" size="lg" disabled={isLoading}>
              {isLoading
                ? "Loading..."
                : `Pay Token ₹${
                    parseInt(mainButtonAmount, 10) +
                    (addPlan && !isToken ? 499 : 0)
                  }`}
            </Button>
          ) : (
            <Button
              onClick={() => {
                setIsBundleBxSelected(false);
                mixpanel.track("enroll_now", {
                  bootcamp: bootcamp?.title,
                  button_location: "bottom_cta",
                  source: "Astrolearn",
                });
              }}
              className="w-full text-lg"
              size="lg"
              disabled={isLoading}
            >
              {isLoading
                ? "Loading..."
                : `Enroll Now ${(
                    (addPlan && !isToken ? 499 : 0) +
                    parseInt(mainButtonAmount) -
                    (couponDiscount ? couponDiscount : 0)
                  ).toLocaleString("en-IN", {
                    style: "currency",
                    currency: "INR",
                    minimumFractionDigits: 0,
                  })}`}
            </Button>
          )}

          {/* ------ Bundle Bootcamp ------ */}
          {bootcamp?.bundleBootcamp && (
            <>
              <p className="font-semibold text-xl text-center">
                {bootcamp?.bundleBootcamp?.title}
              </p>
              <Button
                className="w-full rounded-xl text-lg"
                size={"lg"}
                disabled={isLoading}
                onClick={() => setIsBundleBxSelected(true)}
              >
                {isLoading
                  ? "Loading..."
                  : `Enroll Now ${(
                      (addPlan && !isToken ? 499 : 0) +
                      bootcamp?.bundleBootcamp?.discountedPrice -
                      (couponDiscount ? couponDiscount : 0)
                    ).toLocaleString("en-IN", {
                      style: "currency",
                      currency: "INR",
                      minimumFractionDigits: 0,
                    })}`}
              </Button>
            </>
          )}
        </div>
      </form>
    </Form>
  );
}
